import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ButtonLink, Card, DigitalKey, Sheet, Skeleton, SlideAction } from '@/ds'
import type { KeyAccess } from '@/ds'
import { LockedKey, useKeyDiscrepancy } from '@/app/accesos/LockedKey'
import { useNightKey } from '@/app/accesos/night'
import { useKeyOpener } from '@/app/accesos/useKeyOpener'
import { LinkRow } from '@/app/components/LinkRow'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { featureEnabled } from '@/config/features'
import { dayMonthTime, locale, mapsHref } from '@/lib/format'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { useNow } from '@/lib/useNow'
import { accesosService } from '@/services/accesos'
import type { Access, Directions, KeyStatus } from '@/services/accesos'
import { climaService } from '@/services/clima'
import type { Climate } from '@/services/clima'
import { fianzaService } from '@/services/fianza'
import { pagosService } from '@/services/pagos'
import type { Stay } from '@/services/reserva'
import { useApiLang } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { keyValidity } from '../Estancia/steps'
import { ACCESS_ROUTES, EmergencyLine, emergencyPhone } from './shared'
import { MapEmbed } from './MapEmbed'

/** "Abrir portal", "Abrir puerta del ático" (PantallaAccesos.dc.html). */
const openLabel = (a: Access, t: TFunction) => t('v2.ds.digitalKey.abrir', { name: a.name.toLowerCase() })

function leadFor(key: KeyStatus | null, t: TFunction): string | null {
  if (!key) return null
  if (key.state === 'active') return key.accesses.some((a) => a.code) ? t('v2.accesos.lead.active') : t('v2.accesos.lead.activeSinCodigo')
  return t(`v2.accesos.lead.${key.state}`)
}

/** Portal con telefonillo (app actual OpenDoorGuideView.tsx): pasos 1 y 2 y el botón de abrir. */
function IntercomSheet({ access, onOpen, onClose }: { access: Access | null; onOpen: (id: string) => void; onClose: () => void }) {
  const { t } = useTranslation()
  const steps = access?.intercom
  return (
    <Sheet
      open={access !== null && Boolean(steps)}
      title={access ? t('v2.accesos.interfono.abrir', { name: access.name.toLowerCase() }) : ''}
      confirmHidden
      onCancel={onClose}
    >
      {steps && (
        <div className="accesos__intercom">
          <p>{t('v2.accesos.interfono.texto')}</p>
          <ol>
            <li>
              <span className="accesos__row-title">{t('v2.legacy.preview.labels.portalStepsView.step1')}</span>
              <p className="accesos__row-text">{steps.step1}</p>
              {steps.label && <p className="accesos__row-title">{t('v2.accesos.interfono.piso', { label: steps.label })}</p>}
            </li>
            {steps.step2 && (
              <li>
                <span className="accesos__row-title">{t('v2.legacy.preview.labels.portalStepsView.step2')}</span>
                <p className="accesos__row-text">{steps.step2}</p>
              </li>
            )}
          </ol>
        </div>
      )}
      {access && <SlideAction label={t('v2.ds.digitalKey.deslizaAbrir', { name: access.name.toLowerCase() })} onComplete={() => onOpen(access.id)} />}
    </Sheet>
  )
}

function KeyBlock({ stay, keyStatus, now }: { stay: Stay; keyStatus: KeyStatus; now: Date }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const apiLang = useApiLang()
  const location = useLocation()
  const { states, open } = useKeyOpener(stay)
  // Desde Tu estancia, un portal con telefonillo llega con `state.accessId` para abrir la guía (KeySection).
  const fromHome = (location.state as { accessId?: string } | null)?.accessId ?? null
  const [intercomId, setIntercomId] = useState<string | null>(fromHome)
  const locked = keyStatus.state === 'locked'
  // Motivos (bloqueada) y discrepancia con showDomotic (activa, D6): los mismos datos que Tu estancia.
  const withReasons = locked || keyStatus.state === 'active'
  const [payment] = useAsync(`acc-pay:${stay.guestCode}:${withReasons}`, (signal) => (withReasons ? pagosService.getPaymentSummary(stay, apiLang, signal) : Promise.resolve(null)))
  const [deposit] = useAsync(`acc-dep:${stay.guestCode}:${withReasons}`, (signal) => (withReasons ? fianzaService.getDeposit(stay, apiLang, signal) : Promise.resolve(null)))
  const phone = stay.contact.supportPhone
  // Pantalla nocturna, solo en la llave: modo oscuro del dispositivo o noche en la propiedad (DESIGN.md §2, D41).
  const night = useNightKey(stay.property.timeZone)
  useKeyDiscrepancy(stay, keyStatus, asyncValue(payment), asyncValue(deposit), payment.kind !== 'loading' && deposit.kind !== 'loading', now)

  if (keyStatus.state === 'none') return null
  if (keyStatus.state === 'expired') return <DigitalKey state="expired" />

  if (locked) {
    if (payment.kind === 'loading' || deposit.kind === 'loading') return <Skeleton height={200} radius={24} />
    return <LockedKey stay={stay} keyStatus={keyStatus} payment={asyncValue(payment)} deposit={asyncValue(deposit)} now={now} origin="accesos" />
  }

  const openable = keyStatus.accesses.filter((a) => a.openable)
  const accesses: KeyAccess[] = openable.map((a) => ({ id: a.id, name: a.name, label: openLabel(a, t), state: states[a.id] ?? 'active' }))
  const onOpen = (id: string) => {
    if (keyStatus.accesses.find((a) => a.id === id)?.intercom) setIntercomId(id)
    else void open(id)
  }
  const coded = keyStatus.accesses.find((a) => a.code)
  const showCode = featureEnabled('accesosCodigo')
  const validity = keyValidity(keyStatus, now, lang, t)
  const codeValidity = coded?.code?.validFrom && coded.code.validUntil ? t('v2.accesos.codigo.validez', { from: dayMonthTime(coded.code.validFrom, lang), to: dayMonthTime(coded.code.validUntil, lang) }) : undefined
  return (
    <div className="accesos__section">
      <DigitalKey
        state="active"
        title={stay.unit.name}
        accesses={accesses}
        onOpen={onOpen}
        onRetry={onOpen}
        night={night}
        {...(validity ? { validity } : {})}
        {...(phone ? { phone } : {})}
        {...(showCode && coded?.code ? { code: coded.code.value } : {})}
        {...(showCode && codeValidity ? { codeValidity } : {})}
      />
      {coded && showCode && <LinkRow icon="key" title={t('v2.accesos.codigo.fila')} subtitle={t('v2.accesos.codigo.filaTexto')} to={ACCESS_ROUTES.code} />}
      <IntercomSheet
        access={keyStatus.accesses.find((a) => a.id === intercomId) ?? null}
        onClose={() => setIntercomId(null)}
        onOpen={(id) => {
          setIntercomId(null)
          void open(id)
        }}
      />
    </div>
  )
}

/** Fila "Clima" bajo la llave (prompt-clima.md: sección dentro de Accesos; el control completo en /accesos/clima). */
function ClimateRow({ climate, lang }: { climate: Climate; lang: string }) {
  const { t } = useTranslation()
  if (!climate.available || (climate.variant === 'connected' && climate.state === 'unavailable')) return null
  const zone = climate.zones[0]
  const subtitle =
    climate.variant === 'static'
      ? t('v2.accesos.clima.filaEstatica')
      : climate.state === 'locked'
        ? t('v2.accesos.clima.filaBloqueada')
        : zone?.current != null
          ? t('v2.accesos.clima.filaActiva', { temp: t('v2.estancia.clima.grados', { temp: new Intl.NumberFormat(locale(lang), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(zone.current) }), zone: zone.name.toLowerCase() })
          : undefined
  return <LinkRow icon="thermometer" title={t('v2.accesos.clima.fila')} subtitle={subtitle} to={ACCESS_ROUTES.climate} />
}

/** "Cómo llegar" resumido (PantallaAccesos.dc.html): mapa, dirección, primer medio de transporte y Maps. */
export function DirectionsSummary({ stay, directions }: { stay: Stay; directions: Directions }) {
  const { t } = useTranslation()
  const first = directions.transport[0]
  const more = directions.transport.length > 1 || directions.parking !== null || directions.afterHoursText !== null
  return (
    <section className="accesos__section" aria-labelledby="accesos-como-llegar">
      <h2 id="accesos-como-llegar" className="accesos__h2">
        {t('v2.estancia.comoLlegar.titulo')}
      </h2>
      <Card className="accesos__card">
        <MapEmbed lat={directions.lat} lng={directions.lng} address={[directions.street, directions.locality].filter(Boolean).join(', ')} title={t('v2.accesos.comoLlegar.mapaMarcador', { address: directions.street })} />
        <ul className="accesos__rows">
          <li className="accesos__row">
            <span className="accesos__row-title">{[directions.street, directions.locality].filter(Boolean).join(', ')}</span>
            {directions.addressNote && <p className="accesos__row-text">{directions.addressNote}</p>}
          </li>
          {first && (
            <li className="accesos__row">
              <span className="accesos__row-title">{first.title}</span>
              <p className="accesos__row-text">{first.text}</p>
            </li>
          )}
        </ul>
        <ButtonLink variant="secondary" icon="map-pin" href={mapsHref(directions.lat, directions.lng, stay.unit.address)} target="_blank" rel="noreferrer">
          {t('v2.accesos.comoLlegar.abrirMaps')}
        </ButtonLink>
      </Card>
      {more && <LinkRow icon="car" title={t('v2.accesos.comoLlegar.verTodo')} to={ACCESS_ROUTES.directions} />}
    </section>
  )
}

/**
 * 8.4 · Accesos (PantallaAccesos.dc.html `locked`/`active`, Bloque 4 §4a–4b). Llave real (E1 + E17/E18,
 * `showDomotic` manda, D6); motivos de bloqueo derivados en cliente; código del teclado, transporte y
 * aparcamiento solo con mock. La llave, el código y cómo llegar se guardan para verlos sin conexión (D18).
 */
export function Accesos() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const apiLang = useApiLang()
  const stay = useLoadedStay()
  const now = useNow()
  const cacheKey = `${stay.guestCode}|${stay.phase}`
  const [key, reloadKey] = useAsync(`key:${cacheKey}`, (signal) => accesosService.getKey(stay, signal))
  const [climate] = useAsync(`clima:${cacheKey}:${apiLang}`, (signal) => climaService.getClimate(stay, apiLang, signal))
  const [directions, reloadDirections] = useAsync(`dir:${cacheKey}`, (signal) => accesosService.getDirections(stay, signal))
  const k = asyncValue(key)
  const lead = leadFor(k, t)
  return (
    <main className="page">
      <div className="accesos__head">
        <h1>{t('v2.accesos.titulo')}</h1>
        {lead && <p className="page__lead">{lead}</p>}
      </div>

      {key.kind === 'loading' && (
        <div aria-busy="true">
          <Skeleton height={280} radius={24} />
        </div>
      )}
      {key.kind === 'error' && <SectionError code={key.code} onRetry={reloadKey} />}
      {k && <KeyBlock stay={stay} keyStatus={k} now={now} />}
      {k?.state === 'none' && <LinkRow icon="book" title={t('v2.accesos.instrucciones.fila')} subtitle={t('v2.accesos.instrucciones.filaTexto')} to={ACCESS_ROUTES.instructions} />}

      {climate.kind === 'ready' && <ClimateRow climate={climate.value} lang={lang} />}

      {directions.kind === 'loading' && (
        <div aria-busy="true">
          <Skeleton height={320} />
        </div>
      )}
      {directions.kind === 'error' && <SectionError code={directions.code} onRetry={reloadDirections} />}
      {directions.kind === 'ready' && <DirectionsSummary stay={stay} directions={directions.value} />}

      <EmergencyLine phone={emergencyPhone(stay)} />
    </main>
  )
}

